/** @module util/touches */

// Standard values for WheelEvent.deltaMode
const DOM_DELTA_PIXEL = 0;
const DOM_DELTA_LINE = 1;
const DOM_DELTA_PAGE = 2;

/** Collection of callbacks describing touch actions.
 * @typedef {Object} TouchEvents
 * @prop {function} startSingle - ([px,py]) => void: Called when a single touch starts (i.e., transitioning from no touch or non-single touch)
 * @prop {function} moveSingle - ([px,py]) => void: Called on 'touchmove' if exactly one touch is detected.  Series of moveSingle will always be bracketed by calls to startSingle and endSingle
 * @prop {function} endSingle - () => void: Called when a single touch ends (i.e., on 'touchend' or when transitioning to non-single touch)
 * @prop {function} startDouble - ([px,py],[qx,qy]) => void: Called when a double touch starts (i.e., transitioning from no touch or non-double touch)
 * @prop {function} moveDouble - ([px,py],[qx,qy]) => void: Called on 'touchmove' if at least two touches are detected. Series of moveDouble will always be bracketed by calls to startDouble and endDouble.
 * @prop {function} endDouble - () => void: Called when a double touch ends (i.e., on 'touchend' or when transitioning to non-double touch)
 */

/** Add touchstart/move/end and mousedown/move/up listeners to an HTML element
 * which make it easy to handle single and double touches (e.g. for pinch zoom/rotate).
 * @param {HTMLElement} target - Context for addEventListener calls
 * @param {TouchEvents} touchEvents - Describes events
 */
const addFancyTouchListener = (target, touchEvents) => {
  // wrappers for startSingle/startDouble
  // -------------------
  let startedSingleWithoutEnd = false; // has startSingle been called without yet calling endSingle?
  let startedDoubleWithoutEnd = false; // has startDouble been called without yet calling endDouble?

  const _startSingle = (touchP) => {
    touchEvents.startSingle(touchP);
    startedSingleWithoutEnd = true;
  };

  const _endSingle = () => {
    if (startedSingleWithoutEnd) {
      startedSingleWithoutEnd = false;
      touchEvents.endSingle();
    }
  };

  const _startDouble = (touchP, touchQ) => {
    touchEvents.startDouble(touchP, touchQ);
    startedDoubleWithoutEnd = true;
  };

  const _endDouble = () => {
    if (startedDoubleWithoutEnd) {
      startedDoubleWithoutEnd = false;
      touchEvents.endDouble();
    }
  };
  // -------------------

  let pendingStartSingle = false;

  const doStart = (touchCount, touchP, touchQ) => {
    if (touchCount === 1) {
      // transition from 0 to 1
      _startSingle(touchP);
    } else if (touchCount === 2) {
      // transition from 1 to 2
      _endSingle();
      _startDouble(touchP, touchQ);
    }
  };

  const doMove = (touchCount, touchP, touchQ) => {
    if (touchCount === 1) {
      if (pendingStartSingle) {
        pendingStartSingle = false;
        _startSingle(touchP);
      }
      touchEvents.moveSingle(touchP);
    } else if (touchCount === 2) {
      touchEvents.moveDouble(touchP, touchQ);
    }
  };

  const doEnd = (touchesRemaining) => {
    if (touchesRemaining === 1) {
      // transition from 2 to 1:
      // wait until doMove to startSingle again because the new 'single' point could be either 'p' or 'q'
      _endDouble();
      pendingStartSingle = true;
    } else if (touchesRemaining === 0) {
      // transition from 1 to 0
      _endSingle();
    }
  };

  // event listeners: these should simply pass info to doStart, doMove, doEnd
  // ------------------------------
  const _offsetClientPoint = (x, y) => {
    const rect = target.getBoundingClientRect();
    return [x - rect.left, y - rect.top];
  };

  target.addEventListener(
    'mousedown',
    (e) => {
      // left button mousedown
      const buttons = e.buttons || e.which;
      if (buttons === 1) {
        const touchP = _offsetClientPoint(e.clientX, e.clientY);
        doStart(1, touchP, null);
      }

      e.preventDefault();
    },
    false,
  );

  target.addEventListener(
    'touchstart',
    (e) => {
      if (e.touches.length === 1) {
        // single touch start
        const touchP = _offsetClientPoint(
          e.touches[0].clientX,
          e.touches[0].clientY,
        );
        doStart(1, touchP, null);
      } else if (e.touches.length >= 2) {
        // double touch start
        const touchP = _offsetClientPoint(
          e.touches[0].clientX,
          e.touches[0].clientY,
        );
        const touchQ = _offsetClientPoint(
          e.touches[1].clientX,
          e.touches[1].clientY,
        );
        doStart(2, touchP, touchQ);
      }

      e.preventDefault();
    },
    false,
  );

  target.addEventListener(
    'mousemove',
    (e) => {
      // left button mousedrag
      const buttons = e.buttons || e.which;
      if (buttons === 1) {
        const touchP = _offsetClientPoint(e.clientX, e.clientY);
        doMove(1, touchP, null);
      }

      e.preventDefault();
    },
    false,
  );

  target.addEventListener(
    'touchmove',
    (e) => {
      if (e.touches.length === 1) {
        // single touch move
        const touchP = _offsetClientPoint(
          e.touches[0].clientX,
          e.touches[0].clientY,
        );
        doMove(1, touchP, null);
      } else if (e.touches.length >= 2) {
        // double touch move
        const touchP = _offsetClientPoint(
          e.touches[0].clientX,
          e.touches[0].clientY,
        );
        const touchQ = _offsetClientPoint(
          e.touches[1].clientX,
          e.touches[1].clientY,
        );
        doMove(2, touchP, touchQ);
      }

      e.preventDefault();
    },
    false,
  );

  target.addEventListener(
    'mouseup',
    (e) => {
      const touchesRemaining = 0;

      doEnd(touchesRemaining);
      e.preventDefault();
    },
    false,
  );

  target.addEventListener(
    'touchend',
    (e) => {
      const touchesRemaining = e.touches.length;

      doEnd(touchesRemaining);
      e.preventDefault();
    },
    false,
  );
};

/** Add mousewheel listener to an HTML element.
 * @param {HTMLElement} target - Context for addEventListener calls
 * @param {function} onMousewheel - (dw, [px,py]) => void: Called when wheel moves
 */
const addMousewheelListener = (target, onMousewheel) => {
  target.addEventListener('wheel', (e) => {
    const p = [e.clientX, e.clientY];
    const dyRaw = e.deltaY;
    let dy = 0;
    switch (e.deltaMode) {
      case DOM_DELTA_PIXEL:
        dy = dyRaw;
        break;
      case DOM_DELTA_LINE:
        dy = dyRaw * 16;
        break;
      case DOM_DELTA_PAGE:
        dy = dyRaw * 720;
        break;
    }
    onMousewheel(dy, p);
    e.preventDefault();
  });
};

export { addFancyTouchListener, addMousewheelListener };
